<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <h1>Department Hours Manager</h1>
        <h2>Offices and Departments</h2>
        <p class="description">
          Only authorized personnel are able to manage their departmental hours
          in the
          <a href="https://www.adelphi.edu/departments/" target="_blank">institutional directory</a>. If you have any
          questions, please contact the Digital Team at
          <a href="mailto:digital@adelphi.edu">digital@adelphi.edu</a>.
        </p>
        <div></div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field v-model="search" model-value="John Doe" outlined :style="{ width: '320px' }"
          append-icon="mdi-magnify">
          <template v-slot:label>
            <span class="custom-label">Search Departments</span>
          </template>
        </v-text-field>
        <v-data-table :headers="headers" :items="paginatedDepartments" :items-per-page="itemsPerPage"
          hide-default-footer hide-default-header class="elevation-1" :class="{ 'custom-header': true }">
          <template v-slot:header="{ props: { headers } }">
            <thead>
              <tr>
                <th v-for="h in headers" :key="h.value" class="custom-table-header">
                  <span class="custom-table-header-text">{{ h.text }}</span>
                </th>
              </tr>
            </thead>
          </template>
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="(item, index) in items" :key="item.code" :class="{ 'even-row': index % 2 === 1 }">
                <td class="department-row">
                  <p>{{ item.title }} ({{ item.code }})</p>

                  <v-btn @click="editItem(item)" class="edit-btn">
                    <v-icon left>mdi-pencil</v-icon>
                    Edit</v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-pagination v-model="page" :length="pageCount" :total-visible="7" circle>
        </v-pagination>
      </v-col>
    </v-row>


    <div v-if="isAdmin == true" class="mb-6 mt-6">

      <v-sheet elevation="3" class="my-custom-sheet" rounded>
        <div class="form-title">
          <p class="form-title-text">Global Closures</p>
          <v-icon v-if="showClosures === false" @click="showClosures = true">
            mdi-chevron-down
          </v-icon>
          <v-icon v-if="showClosures === true" @click="showClosures = false">
            mdi-chevron-up
          </v-icon>
        </div>
        <div :style="{ padding: '0px 30px' }" v-if="showClosures === true">
          <v-row class="closure-table-header">
            <v-col cols="6" sm="4" lg="3">
              <span class="header-text">Label</span>
            </v-col>
            <v-col cols="6" sm="4" lg="3">
              <span class="header-text">Text</span>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <span class="header-text">Start Time</span>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <span class="header-text">End Time</span>
            </v-col>
          </v-row>

          <div v-for="(globalClosure, index) in globalClosures" :key="globalClosure.id">
            <v-divider></v-divider>
            <div :style="{ paddingTop: '10px', paddingBottom: '10px', position: 'relative' }">

              <template v-if="editingClosureId === globalClosure.id">
                <v-divider></v-divider>
                <div :style="{ padding: '16px 0px' }">
                  <p>Edit Closure</p>
                  <v-form :ref="`editClosureForm_${globalClosure.id}`">
                    <v-row>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field v-model="editedClosure.label" class="user-container" outlined
                          :rules="[(v) => !!v || 'Label is required']">
                          <template v-slot:label>
                            <span class="custom-label">Closure Label</span>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field v-model="editedClosure.text" class="user-container" outlined
                          :rules="[(v) => !!v || 'Text is required']">
                          <template v-slot:label>
                            <span class="custom-label">Closure Text</span>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="editedClosure.start_time" class="user-container" outlined
                          type="datetime-local" :rules="[(v) => !!v || 'Start time is required']">
                          <template v-slot:label>
                            <span class="custom-label">Start Date & Time</span>
                          </template>
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field v-model="editedClosure.end_time" class="user-container" outlined
                          type="datetime-local" :rules="[
                            (v) => !!v || 'End time is required',
                            (v) => !editedClosure.start_time || new Date(v) > new Date(editedClosure.start_time) || 'End time must be after start time',
                          ]">
                          <template v-slot:label>
                            <span class="custom-label">End Date & Time</span>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="8">
                        <v-select v-model="editedClosure.locations" :items="availableLocations"
                          :rules="[(v) => (v && v.length > 0) || 'At least one location is required']" outlined multiple
                          chips deletable-chips small-chips>
                          <template v-slot:label>
                            <span class="custom-label">Select Locations</span>
                          </template>
                        </v-select>
                      </v-col>

                      <v-col cols="12" md="8">
                        <v-autocomplete v-model="editedClosure.exceptions" :items="exceptionlist" outlined multiple
                          chips deletable-chips small-chips clearable>
                          <template v-slot:label>
                            <span class="custom-label">Select Exceptions</span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>

                    <div class="chip-container" :style="{ gap: '12px' }">
                      <v-btn @click="saveClosure" class="edit-btn">
                        <v-icon left>mdi-check</v-icon>
                        Save
                      </v-btn>


                      <v-btn @click="cancelEdit" class="cancel-btn">
                        <v-icon left>mdi-close</v-icon>
                        Cancel
                      </v-btn>
                    </div>



                  </v-form>
                </div>
              </template>


              <template v-else>
                <v-row align="center" style="padding-right: 10px;">
                  <v-col cols="6" sm="4" lg="3">
                    <p>{{ index + 1 }}. {{ globalClosure.label }}</p>
                  </v-col>
                  <v-col cols="6" sm="4" lg="3">
                    <p>{{ globalClosure.text }}</p>
                  </v-col>
                  <v-col cols="12" sm="6" lg="3">
                    <p>{{ formatDateTime(globalClosure.start_time) }}</p>
                  </v-col>
                  <v-col cols="12" sm="6" lg="3">
                    <p>{{ formatDateTime(globalClosure.end_time) }}</p>
                  </v-col>

                </v-row>




                <v-row>
                  <v-col cols="12" md="6">
                    <div class="info-label">Locations:</div>
                    <div class="chip-container">
                      <!-- Show tooltip only if more than 2 items -->
                      <template v-if="globalClosure.locations && globalClosure.locations.length > 2">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div v-bind="attrs" v-on="on" class="chip-wrapper">
                              <v-chip small class="mr-1" v-for="(loc, i) in globalClosure.locations.slice(0, 2)"
                                :key="i">
                                {{ loc.location }}
                              </v-chip>
                              <v-chip small color="grey lighten-2">
                                +{{ globalClosure.locations.length - 2 }} more
                              </v-chip>
                            </div>
                          </template>
                          <div>
                            <div v-for="(loc, i) in globalClosure.locations" :key="i">
                              • {{ loc.location }}
                            </div>
                          </div>
                        </v-tooltip>
                      </template>

                      <!-- Show chips directly if 1-2 items (no tooltip) -->
                      <template v-else-if="globalClosure.locations && globalClosure.locations.length > 0">
                        <v-chip small class="mr-1" v-for="(loc, i) in globalClosure.locations" :key="i">
                          {{ loc.location }}
                        </v-chip>
                      </template>

                      <!-- Show "None" if empty -->
                      <span v-else class="text-muted">None</span>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div class="info-label">Exceptions:</div>
                    <div class="chip-container">

                      <template v-if="globalClosure.exceptions && globalClosure.exceptions.length > 2">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div v-bind="attrs" v-on="on" class="chip-wrapper">
                              <v-chip small class="mr-1" v-for="(exc, i) in globalClosure.exceptions.slice(0, 2)"
                                :key="i">
                                {{ exc.exception }}
                              </v-chip>
                              <v-chip small color="grey lighten-2">
                                +{{ globalClosure.exceptions.length - 2 }} more
                              </v-chip>
                            </div>
                          </template>
                          <div>
                            <div v-for="(exc, i) in globalClosure.exceptions" :key="i">
                              • {{ exc.exception }}
                            </div>
                          </div>
                        </v-tooltip>
                      </template>


                      <template v-else-if="globalClosure.exceptions && globalClosure.exceptions.length > 0">
                        <v-chip small class="mr-1" v-for="(exc, i) in globalClosure.exceptions" :key="i">
                          {{ exc.exception }}
                        </v-chip>
                      </template>

                      <span v-else class="text-muted">None</span>
                    </div>
                  </v-col>
                </v-row>

                <div style="position: absolute; bottom: 25px; right: 0px;">
                  <div :style="{ display: 'flex', gap: '10px' }">
                    <StatusBar :statusText="getClosureStatus(globalClosure.start_time, globalClosure.end_time)" />

                    <v-menu offset-y v-model="DotActiveMenu[globalClosure.id]">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" @click="dotToggleMenu(globalClosure.id)">
                          mdi-dots-vertical
                        </v-icon>
                      </template>
                      <v-list>
                        <v-list-item @click="editClosure(globalClosure)">
                          <v-list-item-title>Edit</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="deleteClosure(globalClosure.id)">
                          <v-list-item-title>Delete</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>

                </div>
              </template>
            </div>

          </div>


          <v-divider></v-divider>
          <div :style="{
            padding: '16px 0px',
          }">
            <template v-if="startAddNewGlobalClosure">
              <p>Add A New Closure</p>
              <v-form ref="globalClosureForm">
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field v-model="newGlobalClosure.label" class="user-container" outlined :rules="[
                      (v) => !!v || 'Label is required',

                    ]" :style="{}">
                      <template v-slot:label>
                        <span class="custom-label">Closure Label</span>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field v-model="newGlobalClosure.text" class="user-container" outlined :rules="[
                      (v) => !!v || 'Text is required',

                    ]" :style="{}">
                      <template v-slot:label>
                        <span class="custom-label">Closure Text</span>
                      </template>
                    </v-text-field>
                  </v-col>

                </v-row>
                <v-row>

                  <v-col cols="12" md="4">
                    <v-text-field v-model="newGlobalClosure.start_time" class="user-container" outlined
                      type="datetime-local" :rules="[
                        (v) => !!v || 'Start time is required',
                      ]">
                      <template v-slot:label>
                        <span class="custom-label">Start Date & Time</span>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field v-model="newGlobalClosure.end_time" class="user-container" outlined
                      type="datetime-local" :rules="[
                        (v) => !!v || 'end time is required',
                        (v) => !newGlobalClosure.start_time || new Date(v) > new Date(newGlobalClosure.start_time) || 'End time must be after start time',
                      ]">
                      <template v-slot:label>
                        <span class="custom-label">End Date & Time</span>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="8">
                    <v-select v-model="newGlobalClosure.locations" :items="availableLocations"
                      :rules="[(v) => (v && v.length > 0) || 'At least one location is required']" outlined multiple
                      chips deletable-chips small-chips>
                      <template v-slot:label>
                        <span class="custom-label">Select Locations</span>
                      </template>
                    </v-select>
                  </v-col>


                  <v-col cols="12" md="8">

                    <v-autocomplete v-model="newGlobalClosure.exceptions" :items="exceptionlist" outlined multiple chips
                      deletable-chips small-chips clearable>
                      <template v-slot:label>
                        <span class="custom-label">Select Exceptions</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>

                <div class="chip-container" :style="{ gap: '12px', marginBottom: '30px' }">
                  <v-btn @click="handleAddNewGlobalClosure()" class="edit-btn">
                    <v-icon left>mdi-plus-circle-outline</v-icon>
                    Add
                  </v-btn>
                  <v-btn @click="cancelAddNewGlobalClosure()" class="cancel-btn">
                    <v-icon left>mdi-close</v-icon>
                    Cancel
                  </v-btn>
                </div>

              </v-form>
            </template>
            <template v-else>

              <v-btn @click="startAddNewGlobalClosure = true" class="edit-btn">
                <v-icon left>mdi-plus-circle-outline</v-icon>
                Add New
              </v-btn>

            </template>
          </div>


        </div>
      </v-sheet>

    </div>

    <v-row v-if="isAdmin == true">
      <v-col>
        <v-sheet elevation="3" class="my-custom-sheet" rounded>
          <div class="form-title">
            <p class="form-title-text">Hours Tool User Management</p>
            <v-icon v-if="showUsers === false" @click="showUsers = true">
              mdi-chevron-down
            </v-icon>
            <v-icon v-if="showUsers === true" @click="showUsers = false">
              mdi-chevron-up
            </v-icon>
          </div>
          <div :style="{ padding: '0px 30px' }" v-if="showUsers === true">
            <div v-for="(user, index) in departmentScheduleUsers" :key="user.id">
              <v-divider></v-divider>
              <div :style="{ paddingTop: '10px', paddingBottom: '10px' }" class="department-row">
                <p>{{ index + 1 }}. {{ user.email }}</p>
                <div :style="{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '15px',
                }">
                  <v-checkbox v-model="user.all_department_access" label="All Department"
                    @change="handleCheckboxChange(user.id, $event)" class="department-checkbox"></v-checkbox>
                  <v-btn @click="handleShowUserDepartmentCodes(user.id)" class="edit-btn">
                    <v-icon left>mdi-pencil</v-icon>
                    {{
                      showUserDepartmentCodes[user.id] ? "Hide" : "Show"
                    }}</v-btn>
                  <v-btn @click="deleteUser(user.id)" class="delete-btn">
                    <v-icon left color="white">mdi-delete-forever-outline</v-icon>
                    Delete</v-btn>
                </div>
              </div>
              <div v-if="showUserDepartmentCodes[user.id] === true">
                <div v-for="departmentCode in user.department_codes" :key="departmentCode.id" :style="{
                  padding: '10px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                }">
                  <p :style="{
                    margin: 0,
                  }">
                    {{
                      getDepartmentTitleByCode(departmentCode.department_code)
                    }}
                  </p>
                  <v-icon @click="
                    handleDeleteDepartmentCode(user.id, departmentCode.id)
                    ">mdi-close-circle</v-icon>
                </div>

                <div :style="{
                  margin: '10px 20px',
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '20px',
                }">
                  <v-text-field v-model="newDepartmentCode[user.id]" :style="{
                    height: '30px',
                    maxWidth: '300px',
                    padding: '0',
                    margin: '0',
                  }">
                    <template v-slot:label>
                      <span :style="{ color: '#858585', fontWeight: '300' }">New Department Code</span>
                    </template>
                  </v-text-field>
                  <v-btn @click="handleAddNewDepartmentCode(user.id)" class="edit-btn">
                    <v-icon left>mdi-plus-circle-outline</v-icon>
                    Add
                  </v-btn>
                </div>
              </div>
            </div>
            <div :style="{
              padding: '16px 0px',
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
            }">
              <v-text-field v-model="newUserEmail" class="user-container" outlined :rules="[
                (v) => !!v || 'User email is required',
                (v) =>
                  !departmentScheduleUsers.some((user) => user.email === v) ||
                  'Email already exists!',
                (v) =>
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
                  'Must be a valid email address',
              ]" ref="newUser">
                <template v-slot:label>
                  <span class="custom-label">New User Email</span>
                </template>
              </v-text-field>
              <v-btn @click="handleAddNewUser()" class="edit-btn" :style="{ marginBottom: '30px' }">
                <v-icon left>mdi-plus-circle-outline</v-icon>
                Add
              </v-btn>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>
    <Dialog :dialog.sync="deleteUserDialog" @confirm="handleDeleteUser" title="Delete User?"
      message="You will loose all the information entered for this user." btn2Text="DELETE USER" btn1Text="KEEP IT" />
    <Dialog :dialog.sync="showUnsavedChangesDialog" @confirm="confirmSwitchEdit" @cancel="cancelSwitchEdit"
      title="Unsaved Changes" message="You have unsaved changes. Do you want to discard them?" btn2Text="DISCARD & EDIT"
      btn1Text="CANCEL" />
    <Dialog :dialog.sync="deleteClosureConfirmationDialog" @confirm="onDeleteClosureConfirmationChanges"
      title="Delete Closure?" message="You will loose all the information entered for this closure."
      btn2Text="DELETE CLOSURE" btn1Text="KEEP IT" />
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
// import axios from "axios";
import Dialog from "./hourManager/Dialog.vue";
import { getHoursManagerAuth } from "@/services/hoursRoleService";
import apiClient from "../services/apiClient";
import StatusBar from "./hourManager/StatusBar.vue";

export default {
  data() {
    return {

      apiUrl: process.env.VUE_APP_API_BASE_URL,
      search: "",
      page: 1,
      itemsPerPage: 10,
      // dialog: false,
      selectedDepartment: {},
      globalClosures: [],
      headers: [
        { text: "Department", value: "title", sortable: false },
        // { text: "", value: "action", sortable: false },
      ],
      departments: [],
      showUsers: false,
      showUserDepartmentCodes: {},
      newUserEmail: "",
      newGlobalClosure: {
        label: "",
        text: "",
        start_time: null,
        end_time: null,
        locations: [],
        exceptions: []
      },
      availableLocations: ["All", "Garden City Campus", "Manhattan Center", "Hudson Valley Center", "Hauppauge Education and Conference Center",],
      exceptionlist: [],
      isAdmin: false,
      allDepartmentAccess: false,
      departmentScheduleUsers: [],
      deleteUserDialog: false,
      selectedUserId: null,
      newDepartmentCode: {},
      showClosures: false,
      DotActiveMenu: {},
      editingClosureId: null,
      editedClosure: {},
      pendingEditClosure: null,
      showUnsavedChangesDialog: false,
      startAddNewGlobalClosure: false,
      deleteClosureConfirmationDialog: false,
      selectedClosureId: null,
      snackbar: {
        show: false,
        message: '',
        color: 'error'
      }
    };
  },
  components: {
    Dialog,
    StatusBar,
  },

  async created() {

    try {

      await this.fetchDepartmentData();

    } catch (error) {
      window.console.error("Failed to authenticate:", error);
      window.console.error("Error response:", error.response?.data);
    }


  },
  update() { },
  // mounted() {
  //   window.addEventListener("beforeunload", this.clearAccessToken);
  // },
  // beforeDestroy() {
  //   window.removeEventListener("beforeunload", this.clearAccessToken);
  // },
  computed: {
    filteredDepartments() {
      if (this.search) {
        return this.departments.filter((department) =>
          department.title.toLowerCase().includes(this.search.toLowerCase())
        );
      }
      return this.departments;
    },

    paginatedDepartments() {
      return this.filteredDepartments.slice(
        (this.page - 1) * this.itemsPerPage,
        this.page * this.itemsPerPage
      );
    },
    pageCount() {
      return Math.ceil(this.filteredDepartments.length / this.itemsPerPage);
    },
  },
  methods: {

    async fetchDepartmentData() {
      try {

        this.authData = await getHoursManagerAuth();
        const { isAdmin, allDepartmentAccess, userDepartments } = this.authData;
        this.isAdmin = isAdmin;
        this.allDepartmentAccess = allDepartmentAccess;

        const response = await apiClient.get(
          `/v1/departments/?no_pagination=true`
        );


        const filteredData = response.data.filter((d) => d.active === true);
        if (this.isAdmin || this.allDepartmentAccess) {
          this.departments = filteredData;
        } else if (!this.allDepartmentAccess) {

          this.departments = filteredData.filter((data) =>
            userDepartments.some(
              (department) => department.department_code === data.code
            )
          );
        }
   
        this.exceptionlist = this.departments.map(department => department.title)
        const response2 = await apiClient.get(
          `/v1/department_schedule_user/?no_pagination=true`
        );

        this.departmentScheduleUsers = response2.data;

        const response3 = await apiClient.get(
          `/v1/global_closures/?no_pagination=true`
        );
        this.globalClosures = response3.data
        // this.globalClosures = response3.data.map(d=>{
        //   return {

        //   }
        // });
        // window.console.log("globalClosures:", this.globalClosures);

      } catch (error) {
        window.console.error("Error fetching data:", error);
      }
    },
    editItem(item) {
      // this.selectedDepartment = { ...item };
      // this.dialog = true;
      this.$router.push({
        path: `/office_hour_manager/${item.id}`,
        query: { department: encodeURIComponent(JSON.stringify(item)) },
      });
    },
    updateDepartment(updatedDepartment) {
      const index = this.departments.findIndex(
        (department) => department.title === updatedDepartment.title
      );
      if (index !== -1) {
        this.departments.splice(index, 1, updatedDepartment);
      }
    },
    updatePage(page) {
      this.page = page;
    },



    async deleteUser(userId) {
      this.selectedUserId = userId;
      this.deleteUserDialog = true;
    },
    handleShowUserDepartmentCodes(userId) {
      if (this.showUserDepartmentCodes[userId] !== undefined) {
        this.showUserDepartmentCodes[userId] =
          !this.showUserDepartmentCodes[userId];
      } else {
        this.$set(this.showUserDepartmentCodes, userId, true);
      }
    },
    async handleAddNewUser() {
      if (!this.$refs.newUser.validate()) {
        this.$nextTick(() => {
          const element = this.$refs.newUser;
          const domElement = element.$el || element;

          if (domElement.scrollIntoView) {
            domElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
          if (typeof domElement.focus === "function") {
            domElement.focus();
          }
        });
      } else {
        try {
          const newPost = {
            email: this.newUserEmail,
            department_codes: [],
          };

          const response = await apiClient.post(
            '/v1/department_schedule_user/',
            newPost
          );

          if (response.data) {
            this.departmentScheduleUsers = [
              ...this.departmentScheduleUsers,
              response.data,
            ];
            this.newUserEmail = "";
            // Reset the text field validation state
            this.$refs.newUser.reset();
            this.showSuccess('User added successfully');
          }
        } catch (error) {
          window.console.error("Error adding new user email", error);
          if (error.response) {
            // Server responded with error status
            const status = error.response.status;
            const message = error.response.data?.detail || error.response.data?.message;

            if (status === 400) {
              this.showError(message || 'Invalid user data. Please check the email.');
            } else if (status === 403) {
              this.showError('You do not have permission to add users.');
            } else if (status === 500) {
              this.showError('Server error. Please try again later.');
            } else {
              this.showError(message || 'Failed to add user. Please try again.');
            }
          } else if (error.request) {
            // Request made but no response
            this.showError('Network error. Please check your connection.');
          } else {
            // Something else happened
            this.showError('An unexpected error occurred.');
          }

        }
      }
    },

    async handleAddNewGlobalClosure() {
      if (!this.$refs.globalClosureForm.validate()) return;


      try {
        const payload = {
          label: this.newGlobalClosure.label,
          text: this.newGlobalClosure.text,
          start_time: this.newGlobalClosure.start_time,
          end_time: this.newGlobalClosure.end_time,
          locations: this.newGlobalClosure.locations.map(loc => ({ location: loc })),
          exceptions: this.newGlobalClosure.exceptions.map(exc => ({ exception: exc }))
        };

        const response = await apiClient.post('/v1/global_closures/', payload);
        const data = response.data;

        this.globalClosures.push(data);
        this.cancelAddNewGlobalClosure();
        this.showSuccess('Closure added successfully');
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const message = error.response.data?.detail ||
            error.response.data?.message ||
            error.response.data?.error;

          if (status === 400) {
            this.showError(message || 'Invalid data. Please check your input.');
          } else if (status === 401) {
            this.showError('Authentication required. Please log in again.');
          } else if (status === 403) {
            this.showError('You do not have permission to add closures.');
          } else if (status === 500) {
            this.showError('Server error. Please try again later.');
          } else {
            this.showError(message || 'Failed to add closure. Please try again.');
          }
          // Request was made but no response received (network error)  
        } else if (error.request) {
          this.showError('Network error. Please check your connection.');
          // Something else went wrong
        } else {
          this.showError('An unexpected error occurred.');
        }

      }
    },
    cancelAddNewGlobalClosure() {
      this.newGlobalClosure = {
        label: "",
        text: "",
        start_time: null,
        end_time: null,
        locations: [],
        exceptions: []
      }
      this.$refs.globalClosureForm.reset();
      this.startAddNewGlobalClosure = false
    },
    getDepartmentTitleByCode(code) {
      const department = this.departments.find((d) => d.code === code);
      return department ? `${department.title} (${department.code})` : code;
    },
    async handleDeleteDepartmentCode(userId, codeId) {


      try {
        const response = await apiClient.delete(
          `${this.apiUrl}/v1/department_schedule_user/${userId}/department_codes/${codeId}/`,

        );

        if (response.status === 204) {
          const newUsers = this.departmentScheduleUsers.map((user) => {
            if (user.id === userId) {
              return {
                ...user,
                department_codes: user.department_codes.filter(
                  (code) => code.id !== codeId
                ),
              };
            }
            return user;
          });
          this.departmentScheduleUsers = newUsers;
          this.showSuccess('Department code removed successfully');
        }
      } catch (error) {
        window.console.error("Error deleting the departmentcode:", error);
        if (error.response?.status === 404) {
          this.showError('Department code not found.');
        } else if (error.response?.status === 403) {
          this.showError('You do not have permission to delete this code.');
        } else {
          this.showError('Failed to delete department code. Please try again.');
        }
      }
    },
    async handleDeleteUser() {
      this.deleteUserDialog = false;

      try {
        const response = await apiClient.delete(
          `${this.apiUrl}/v1/department_schedule_user/${this.selectedUserId}/`

        );
        if (response.status === 204) {
          const newUsers = this.departmentScheduleUsers.filter(
            (user) => user.id !== this.selectedUserId
          );
          this.departmentScheduleUsers = newUsers;
          this.showSuccess('User deleted successfully');
        }
      } catch (error) {
        window.console.error("Error deleting the user:", error);
        this.showError('Failed to delete user. Please try again.');
        // Handle the error, e.g., show a notification to the user
      }
    },
    async handleAddNewDepartmentCode(userId) {
      if (!this.newDepartmentCode[userId]) {
        return;
      }

      const newPost = {
        department_code: this.newDepartmentCode[userId],
      };
      try {
        const response = await apiClient.post(
          `${this.apiUrl}/v1/department_schedule_user/${userId}/department_codes/`,
          newPost
        );

        if (response.data) {
          const newUsers = this.departmentScheduleUsers.map((users) => {
            if (users.id === userId) {
              return {
                ...users,
                department_codes: [...users.department_codes, response.data],
              };
            }
            return users;
          });
          this.departmentScheduleUsers = newUsers;
          this.newDepartmentCode[userId] = "";
        }
      } catch (error) {
        window.console.error("Error posting the department code:", error);
        // Handle the error, e.g., show a notification to the user
      }
    },


    async handleCheckboxChange(userId, newValue) {

      const payload = {
        all_department_access: newValue,
      };
      try {
        const response = await apiClient.patch(
          `${this.apiUrl}/v1/department_schedule_user/${userId}/`,
          payload

        );
        if (response.status >= 200 && response.status < 300) {
          const newUsers = this.departmentScheduleUsers.map(
            (user) => {
              if (user.id === userId) {
                return {
                  ...user,
                  all_department_access: newValue,
                }
              }
              return user;
            }
          );
          this.departmentScheduleUsers = newUsers;
        }
      } catch (error) {
        window.console.error("Error updating the user:", error);
        // Handle the error, e.g., show a notification to the user
      }
    },
    showError(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'error'
      };
    },

    showSuccess(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'success'
      };
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';

      const localString = dateTimeString.replace('Z', '');
      const date = new Date(localString);
      return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

    },
    formatDateTimeLocal(isoString) {
      if (!isoString) return null;
      const date = new Date(isoString.replace('Z', ''));
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },
    dotToggleMenu(key) {
      if (!this.DotActiveMenu.hasOwnProperty(key)) {
        this.$set(this.DotActiveMenu, key, false);
      }
      this.$set(this.DotActiveMenu, key, !this.DotActiveMenu[key]);
    },

    editClosure(closure) {
      // Check if another row is being edited
      if (this.editingClosureId !== null && this.editingClosureId !== closure.id) {
        // Store the pending closure and show dialog
        this.pendingEditClosure = closure;
        this.showUnsavedChangesDialog = true;
        return;
      }

      this.startEditingClosure(closure);
    },

    startEditingClosure(closure) {
      this.editingClosureId = closure.id;
      // Create a deep copy to avoid mutating original



      this.editedClosure = {
        ...closure,
        label: closure.label,
        text: closure.text,
        start_time: this.formatDateTimeLocal(closure.start_time),
        end_time: this.formatDateTimeLocal(closure.end_time),
        locations: closure.locations ? closure.locations.map(loc => loc.location) : [],
        exceptions: closure.exceptions ? closure.exceptions.map(exc => exc.exception) : [],
      };

      // Close the menu
      this.$set(this.DotActiveMenu, closure.id, false);
    },

    confirmSwitchEdit() {
      this.showUnsavedChangesDialog = false;
      this.cancelEdit();
      if (this.pendingEditClosure) {
        this.startEditingClosure(this.pendingEditClosure);
        this.pendingEditClosure = null;
      }
    },

    cancelSwitchEdit() {
      this.showUnsavedChangesDialog = false;
      this.pendingEditClosure = null;
    },

    cancelEdit() {
      this.editingClosureId = null;
      this.editedClosure = {};
    },

    async saveClosure() {
 
      const form = this.$refs[`editClosureForm_${this.editingClosureId}`];
      //vue 2 treat ref inside array as an array of refs
      if (!form[0] || !form[0].validate()) return;

      try {
        const payload = {
          label: this.editedClosure.label,
          text: this.editedClosure.text,
          start_time: this.editedClosure.start_time,
          end_time: this.editedClosure.end_time,
          locations: this.editedClosure.locations.map(loc =>
            typeof loc === 'string' ? { location: loc } : loc
          ),
          exceptions: this.editedClosure.exceptions.map(exc =>
            typeof exc === 'string' ? { exception: exc } : exc
          ),
        };

        const response = await apiClient.patch(
          `/v1/global_closures/${this.editingClosureId}/`,
          payload
        );

        if (response.data) {
          // Update the closure in the list
          const index = this.globalClosures.findIndex(
            c => c.id === this.editingClosureId
          );
          if (index !== -1) {
            this.$set(this.globalClosures, index, response.data);
          }
          this.showSuccess('Closure updated successfully');
          this.cancelEdit();
        }
      } catch (error) {
        window.console.error("Error updating closure:", error);
        this.showError('Failed to update closure. Please try again.');
      }
    },
    async deleteClosure(closureId) {
      this.selectedClosureId = closureId;
      this.deleteClosureConfirmationDialog = true;


    },
    async onDeleteClosureConfirmationChanges() {
      this.deleteClosureConfirmationDialog = false;

      try {
        const response = await apiClient.delete(
          `/v1/global_closures/${this.selectedClosureId}/`
        );

        if (response.status === 204) {
          this.globalClosures = this.globalClosures.filter(
            c => c.id !== this.selectedClosureId
          );
          this.selectedClosureId = null;
          this.showSuccess('Closure deleted successfully');
        }
      } catch (error) {
        window.console.error("Error deleting closure:", error);
        this.showError('Failed to delete closure. Please try again.');
      }
    },
    getClosureStatus(start_time, end_time) {

      const now = new Date();
      const nowAsUTC = new Date(Date.UTC(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      ));

      const start = new Date(start_time);
      const end = new Date(end_time);

      if (nowAsUTC < start) {
        return "upcoming";
      } else if (nowAsUTC > end) {
        return "expired";
      } else {
        return "active";
      }

    },
  },
  watch: {
    search() {
      this.page = 1;
    },
    'newGlobalClosure.locations': {
      handler(newVal, oldVal) {
        if (!newVal) return;
        const allJustSelected = newVal.includes("All") && (!oldVal || !oldVal.includes("All"));
        if (allJustSelected) {
          this.$nextTick(() => {
            this.newGlobalClosure.locations = ["All"];
          });
        }

        else if (oldVal && oldVal.includes("All") && newVal.length > 1) {

          this.$nextTick(() => {
            this.newGlobalClosure.locations = newVal.filter(loc => loc !== "All");
          });
        }
      },
      deep: true
    },
    'editedClosure.locations': {
      handler(newVal, oldVal) {
        if (!newVal) return;
        const allJustSelected = newVal.includes("All") && (!oldVal || !oldVal.includes("All"));
        if (allJustSelected) {
          this.$nextTick(() => {
            this.editedClosure.locations = ["All"];
          });
        }

        else if (oldVal && oldVal.includes("All") && newVal.length > 1) {

          this.$nextTick(() => {
            this.editedClosure.locations = newVal.filter(loc => loc !== "All");
          });
        }
      },
      deep: true
    }
  },
};
</script>

<style scoped>
h1 {
  color: #000 !important;
  font-family: Graphik !important;
  font-size: 53px !important;
  font-style: normal !important;
  font-weight: 900 !important;
  line-height: normal !important;
}

h2 {
  color: #000 !important;
  font-family: Graphik !important;
  font-size: 30px !important;
  font-style: normal !important;
  font-weight: 300 !important;
  line-height: normal !important;
}

p {
  margin: 0;
  font-family: Graphik !important;
}

.description {
  color: #85817c !important;
  font-family: Graphik !important;
  font-size: 16px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: 34px !important;
}

.description a {
  color: black !important;
  font-weight: 900 !important;
  text-decoration: underline;
}

.custom-label {
  color: #85817c !important;
  font-family: Graphik;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
}

.v-data-table {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.department-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.department-row p {
  margin: 0;
}

.v-btn--active {
  background-color: #1976d2;
  color: white;
}

.custom-table-header {
  background-color: #f1efed;
  box-shadow: 0px -1px 0px 0px #edf2f7 inset;
}

.custom-table-header:first-child {
  border-top-left-radius: 16px;
}

.custom-table-header:last-child {
  border-top-right-radius: 16px;
}

.custom-table-header-text {
  color: #4f2c1d;
  font-family: Graphik;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.even-row {
  background-color: #f5f5f5;
  /* Light gray background */
}

.edit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffb500 !important;
  height: 30px !important;
}

.delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(119, 119, 118, 1) !important;
  height: 30px !important;
  color: white;
}

.my-custom-sheet {
  background-color: rgba(0, 0, 0, 0.03) !important;
  border-radius: 8px;
  width: 100% !important;
}

.form-title {
  padding: 16px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-title-text {
  color: var(--black-087, rgba(0, 0, 0, 0.87));
  font-family: Graphik;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
  /* 160% */
  letter-spacing: 0.25px;
  margin: 0;
}



.user-container {
  max-width: 300px;
}

.user-container>>>.v-input__slot {
  background-color: white;
  border-radius: 8px;
}


.department-checkbox>>>.v-input__slot {
  margin: 0px;
}

.department-checkbox>>>.v-messages {
  display: none;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.chip-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.chip-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  cursor: pointer;
}

.text-muted {
  color: #999;
  font-style: italic;
  font-size: 14px;
}

.closure-table-header {
  background-color: #f1efed;
  padding: 12px 0;
  margin: 0 0 8px 0;
  border-radius: 8px 8px 0 0;
}

.header-text {
  color: #4f2c1d;
  font-family: Graphik;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.cancel-btn {
  background-color: white;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px !important;
}

.gap-2 {
  gap: 8px;
}

.d-flex {
  display: flex;
}

.v-tooltip__content {
  background-color: rgba(97, 97, 97, 1) !important;
  opacity: 1 !important;
}
</style>
